import get from 'lodash/get';
import flatten from 'lodash/flatten';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
// @ts-ignore This directive was added as part of a bulk-change to enable noImplicitAny.
import * as ucumLhc from '@lhncbc/ucum-lhc';

/**
 * the ucum lib has two different types of suggestions, this normalizes them into a common type
 */
export class NormalizedSuggestions {
  static initFromSuggestion(
    ucumSuggestionsUnits: ucumLhc.UnitSuggestion = [] as ucumLhc.UnitSuggestion,
  ) {
    const [value, display, description] =
      ucumSuggestionsUnits || ([] as ucumLhc.UnitSuggestion);

    return new NormalizedSuggestions(value, display, description);
  }

  static initFromValidUnit(
    unit: ucumLhc.UnitResult = {} as ucumLhc.UnitResult,
  ) {
    const { code, name, guidance } = unit || ({} as ucumLhc.UnitResult);

    return new NormalizedSuggestions(code, name, guidance);
  }

  public readonly value: string;
  public readonly display: string;
  public readonly description: string;

  constructor(value: string, display: string, description: string) {
    this.value = value;
    this.display = display || this.value; // % doesn't return a display, default to value if it exists
    this.description = description;
  }
}

const ucumUtils = ucumLhc.UcumLhcUtils.getInstance();

// validation can be expensive and inputs will likely be repeated often.  Create memoization cache
const validateMemoizationCache: {
  [key: string]: ucumLhc.ValidateUnitStringResult;
} = {};

const validateUnit = (unit: string | number) => {
  if (validateMemoizationCache[unit]) return validateMemoizationCache[unit];

  // input could be just a number string which is technically a valid unit, but not for this case
  let result = null;
  if (isNaN(unit as number)) {
    result = ucumUtils.validateUnitString(unit as string, true);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
  validateMemoizationCache[unit] = result;
  return result;
};

/**
 * Standardize input.  Returns null if unable to standardize
 */
export const normalizeInput = (input: any): string => {
  if (isNil(input) || !isString(input)) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return null;
  }

  const withoutWhitespace = input.replace(/\s/g, '');

  // input could be just a number string which is technically a valid unit, but not for this case
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return isEmpty(withoutWhitespace) || !isNaN(withoutWhitespace as any)
    ? null
    : withoutWhitespace;
};

/**
 * Checks if the input is an exact ucum code or can be inferred as a ucum code
 * @param {string} unit - raw input
 */
export const isUnitValid = (unit: string) => {
  const scrubbedInput = normalizeInput(unit);
  if (!scrubbedInput) return false;

  const validationResponse = validateUnit(scrubbedInput);
  // ucum code may be invalid, but if it has a single suggestion we will use that
  return !!get<ucumLhc.ValidateUnitStringResult, 'ucumCode', string>(
    validationResponse,
    'ucumCode',
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    null,
  );
};

/**
 * returns the validation error message for a unit.  returns null if no message or valid
 */
export const getValidationErrorMessage = (unit: string) => {
  if (isUnitValid(unit)) return null;

  const scrubbedInput = normalizeInput(unit);
  const validationResponse = validateUnit(scrubbedInput);
  const messages = get(validationResponse, 'msg', []);

  return messages.length ? messages.join(', ') : null;
};

/**
 * Gets the friendly name for a unit.  Returns null if invalid
 */
export const getFriendlyNameForUnit = (unit: string) => {
  const scrubbedInput = normalizeInput(unit);

  if (!scrubbedInput) return null;

  const validatedUnit = validateUnit(scrubbedInput);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
  if (!validateUnit || !validatedUnit.unit) return null;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
  return validatedUnit.unit.name || validatedUnit.unit.code || null;
};

/**
 * returns the validation error message for a unit.  returns null if no message or valid
 */
const suggestionMemoizationCache: {
  [key: string]: NormalizedSuggestions[];
} = {};
export const getSuggestions = (unit: string) => {
  const scrubbedInput = normalizeInput(unit);

  if (suggestionMemoizationCache[scrubbedInput]) {
    return suggestionMemoizationCache[scrubbedInput];
  }

  const validationResponse = validateUnit(scrubbedInput);

  // if this is valid, it will be returned as a value and not a suggestion
  const validationResponseUnit = get<
    ucumLhc.ValidateUnitStringResult,
    'unit',
    ucumLhc.UnitResult
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
  >(validationResponse, 'unit', null);
  const validValueSuggestion = NormalizedSuggestions.initFromValidUnit(
    validationResponseUnit,
  );

  // if not valid, suggestions may be provided
  const suggestionsUnits = get<
    ucumLhc.ValidateUnitStringResult,
    'suggestions',
    ucumLhc.Suggestion[]
  >(validationResponse, 'suggestions', []).map(
    // @ts-ignore This directive was added as part of a bulk-change to enable noImplicitAny.
    suggestion =>
      get<ucumLhc.Suggestion, 'units', ucumLhc.UnitSuggestion[]>(
        suggestion,
        'units',
        [],
      ),
  );

  const normalizedSuggestions = flatten(suggestionsUnits).map(rawSuggestion =>
    NormalizedSuggestions.initFromSuggestion(rawSuggestion),
  );

  const returnValue = [validValueSuggestion, ...normalizedSuggestions].filter(
    suggestion => !!suggestion && !!suggestion.value,
  );

  suggestionMemoizationCache[scrubbedInput] = returnValue;
  return returnValue;
};

/**
 * returns the ucum code for the raw input
 */
export const getUcumCode = (unit: string): string => {
  const scrubbedInput = normalizeInput(unit);

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  if (!isUnitValid(scrubbedInput)) return null;

  const validationResponse = validateUnit(scrubbedInput);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
  return validationResponse.ucumCode || null;
};

// conversion checks can be expensive and inputs will likely be repeated often.  Create memoization cache
const conversionCheckMemoizationCache: { [key: string]: boolean } = {};
export const canConvertBetweenUnits = (unit1: string, unit2: string) => {
  const scrubbed1 = normalizeInput(unit1);
  const scrubbed2 = normalizeInput(unit2);

  if (
    !scrubbed1 ||
    !scrubbed2 ||
    !isUnitValid(scrubbed1) ||
    !isUnitValid(scrubbed2)
  ) {
    return false;
  }

  const code1 = getUcumCode(scrubbed1);
  const code2 = getUcumCode(scrubbed2);

  if (!code1 || !code2) return false;

  if (code1 === code2) return true;

  // sort to keep consistent memoization key
  const memoizationKey = [scrubbed1, scrubbed2].sort().join('|');

  const conversionMemoized = !isNil(
    conversionCheckMemoizationCache[memoizationKey],
  );

  // conversion check never run
  if (!conversionMemoized) {
    const conversionResult = ucumUtils.convertUnitTo(unit1, 1, unit2, false);
    const conversionValid =
      !!conversionResult && conversionResult.status === 'succeeded';
    conversionCheckMemoizationCache[memoizationKey] = conversionValid;
  }

  return conversionCheckMemoizationCache[memoizationKey];
};

// conversions can be expensive and inputs will likely be repeated often.  Create memoization cache
const conversionMemoizationCache: { [key: string]: number } = {};

/**
 * Converts a quanitty of one unit to another.  Returns null if unable to run conversion
 */
export const convertUnits = (
  fromUnit: string,
  value: number,
  toUnit: string,
) => {
  const invalidQuantity = isNil(value) || !isNumber(value);

  if (!canConvertBetweenUnits(fromUnit, toUnit) || invalidQuantity) return null;

  const scrubbedFrom = normalizeInput(fromUnit);
  const scrubbedTo = normalizeInput(toUnit);

  if (
    !scrubbedFrom ||
    !scrubbedTo ||
    !isUnitValid(scrubbedFrom) ||
    !isUnitValid(scrubbedTo)
  ) {
    return null;
  }

  const fromCode = getUcumCode(scrubbedFrom);
  const toCode = getUcumCode(scrubbedTo);

  if (!fromCode || !toCode) return null;

  if (fromCode === toCode) return value;

  // sort to keep consistent memoization key
  const memoizationKey = `${fromCode}|${toCode}|${toUnit}|${value}`;

  const conversionMemoized =
    conversionMemoizationCache[memoizationKey] !== undefined;

  // conversion check never run
  if (!conversionMemoized) {
    const conversionResult = ucumUtils.convertUnitTo(
      fromCode,
      value,
      toCode,
      false,
    );
    const conversionValid =
      !!conversionResult &&
      conversionResult.status === 'succeeded' &&
      isNumber(conversionResult.toVal);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
    conversionMemoizationCache[memoizationKey] = conversionValid
      ? conversionResult.toVal
      : null;
  }

  return conversionMemoizationCache[memoizationKey];
};

export const unitToQuantity = (unit: string, value?: number): fhir.Quantity => {
  const commonProps = {
    unit: getFriendlyNameForUnit(unit) || unit,
    system: 'http://unitsofmeasure.org',
    code: getUcumCode(unit) || unit,
  };

  return isNil(value) ? commonProps : { ...commonProps, value };
};
